import { MailOutlined } from "@ant-design/icons";
import { useResendVerificationByEmail } from "@bodhistreams/queries/src/useAuthQueries";
import { Button, theme } from "antd";
import { useEffect, useState } from "react";

const { useToken } = theme;

/** Matches RESEND_VERIFICATION_COOLDOWN_SECS on the server. */
const RESEND_COOLDOWN_SECS = 60;

/**
 * Shown inside the auth glass card when a correct password hits an account
 * whose email was never verified. Explains the situation and offers a resend
 * with the same cooldown the server enforces, so the button never invites a
 * request that would be refused.
 *
 * @param {{ email: string, onBack: () => void }} props
 */
const EmailNotVerified = ({ email, onBack }) => {
	const { token } = useToken();
	const { mutateAsync: resend, isPending } = useResendVerificationByEmail();
	const [cooldown, setCooldown] = useState(0);

	useEffect(() => {
		if (cooldown <= 0) return undefined;
		const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
		return () => clearTimeout(timer);
	}, [cooldown]);

	const handleResend = async () => {
		try {
			await resend(email);
			setCooldown(RESEND_COOLDOWN_SECS);
		} catch (err) {
			// A 429 means a mail went out moments ago; mirror the server's wait.
			if (err?.response?.status === 429) setCooldown(RESEND_COOLDOWN_SECS);
		}
	};

	return (
		<div style={{ textAlign: "center" }}>
			<div
				className="auth-logo-ring"
				style={{ margin: "0 auto 18px", animation: "none" }}
			>
				<MailOutlined style={{ fontSize: 30, color: token.colorPrimary }} />
			</div>

			<h2 className="auth-title">Verify your email first</h2>
			<p className="auth-subtitle" style={{ marginTop: 10, lineHeight: 1.6 }}>
				Your password is correct, but this account has not confirmed its email
				address yet. We sent a verification link to
			</p>
			<p
				className="auth-title"
				style={{ fontSize: 15, marginTop: 6, wordBreak: "break-all" }}
			>
				{email}
			</p>
			<p className="auth-subtitle" style={{ marginTop: 10, lineHeight: 1.6 }}>
				Open that email and click the link, then come back and sign in. If you
				cannot find it, check your spam folder or request a new one below.
			</p>

			<Button
				type="primary"
				className="auth-submit-btn"
				style={{ marginTop: 22 }}
				onClick={handleResend}
				loading={isPending}
				disabled={cooldown > 0}
			>
				{cooldown > 0
					? `Email sent. Resend available in ${cooldown}s`
					: "Resend verification email"}
			</Button>

			<div className="auth-footer">
				<Button
					type="link"
					onClick={onBack}
					style={{
						padding: 0,
						height: "auto",
						fontSize: 13,
						color: token.colorPrimary,
					}}
				>
					Back to sign in
				</Button>
			</div>
		</div>
	);
};

export default EmailNotVerified;
